/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Widget reports constructor component
 *
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import type { FC } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { icons } from '@assets/index';
import { Button, Skeleton } from '@components/common';
import { FlexColumnMiddleStyled, FlexMiddleStyled } from '@components/common/Block/Flexbox';
import { ImgStyled } from '@components/common/Block/Img';
import { LayoutStyled } from '@components/common/Block/Layout';
import { makeSelectWidgetReportsConstractorTranslation } from '@modules/internationalization/intSelectors';
import { makeSelectAuthUserData, makeSelectIsSingleAccount } from '@pages/PageAuth/selectors/authSelectors';
import { EReportsConstructorSource } from '@pages/PageReportsConstructor/constants';
import { IReportsConstructorLocationData } from '@pages/PageReportsConstructor/types';
import { RoutePath } from '@routes/constants/routesConstants';

import { WidgetSkeletonButton, WidgetSkeletonParagraph } from '../constants/widgetSkeletonContants';
import { WidgetHelpTitleStyled } from '../styled/WidgetHelpStyled';
import { WidgetEmptyCircleStyled, WidgetTitleStyled } from '../styled/WidgetStyled';
import { makeSelectWidgetSearchAccountsSelectedAccount } from '../WidgetSearchAccounts/widgetSearchAccountsSelectors';
import {
    WidgetReportsConstructorContainerStyled,
    WidgetReportsConstructorLabelStyled,
    WidgetReportsConstructorTextStyled,
} from './styled/WidgetReportsConstructorStyled';

export const WidgetReportsConstructor: FC = () => {
    const history = useHistory<IReportsConstructorLocationData>();

    const isSingleAccount = useSelector(makeSelectIsSingleAccount());
    const tReportsConstructor = useSelector(makeSelectWidgetReportsConstractorTranslation());
    const userBisId = useSelector(makeSelectAuthUserData()).bisId;
    const selectedAccountId = useSelector(makeSelectWidgetSearchAccountsSelectedAccount()).id;

    const handleOnClick = () => {
        const locationData: IReportsConstructorLocationData = {
            source: EReportsConstructorSource.WIDGET,
            bisAccountId: isSingleAccount ? userBisId : selectedAccountId,
        };

        history.push(RoutePath.reportsConstructor, locationData);
    };

    return (
        <FlexColumnMiddleStyled>
            <WidgetReportsConstructorContainerStyled data-widget="WidgetReportsConstructor">
                <LayoutStyled className="mb-4">
                    <WidgetTitleStyled>{tReportsConstructor.widgetTitle}</WidgetTitleStyled>
                </LayoutStyled>
                <Skeleton count={3} isLoading={!tReportsConstructor} render={() => WidgetSkeletonParagraph}>
                    <FlexColumnMiddleStyled>
                        <WidgetEmptyCircleStyled>
                            <ImgStyled className="medium" src={icons.transactionMediumIcon} />
                        </WidgetEmptyCircleStyled>
                        <WidgetReportsConstructorLabelStyled>
                            <WidgetHelpTitleStyled>{tReportsConstructor.title}</WidgetHelpTitleStyled>
                        </WidgetReportsConstructorLabelStyled>
                        <WidgetReportsConstructorTextStyled>
                            {tReportsConstructor.text}
                        </WidgetReportsConstructorTextStyled>
                    </FlexColumnMiddleStyled>
                </Skeleton>
                <Skeleton isLoading={!tReportsConstructor} render={() => WidgetSkeletonButton}>
                    <LayoutStyled className="mt-3">
                        <FlexMiddleStyled>
                            <Button className="widget-reports-constructor__button" onClick={handleOnClick}>
                                {tReportsConstructor.btn}
                            </Button>
                        </FlexMiddleStyled>
                    </LayoutStyled>
                </Skeleton>
            </WidgetReportsConstructorContainerStyled>
        </FlexColumnMiddleStyled>
    );
};
